import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { SpaceType } from "building-types/rooms/spaceTypesEnum";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { polyLine2Area } from "geom/polyLine2";
import type { RoomSetSpaceTypes, SpaceClassifierTables } from "./electricalSpaceTypes";
import { electricalSpaceClassifiers } from "./electricalSpaceTypes";

// classify each room type as a space type based on the other rooms in an open connected room set

export function getRoomSetSpaceTypes(
  roomSet: RoomSet,
  rooms: Table<Room>,
  tables: SpaceClassifierTables,
): RoomSetSpaceTypes {
  const roomCount = roomSet.length;
  const roomSpaceTypes: { [roomId: string]: SpaceType } = {};

  if (roomCount === 1) {
    const spaceType = electricalSpaceClassifiers[roomSet[0].type].is;
    roomSpaceTypes[roomSet[0].id] = spaceType;
    return { majoritySpaceType: spaceType, roomSpaceTypes };
  }

  const spaceTypeScores: {
    [key in SpaceType]?: number;
  } = {};
  const { primaryClassifier, secondaryClassifier } = tables;

  for (const { type: roomType, id } of roomSet) {
    const primaryResult = primaryClassifier[roomType];
    if (!primaryResult) {
      continue;
    }

    const { is } = primaryResult;
    roomSpaceTypes[id] = is;
    const { outline } = rooms[id];
    const area = outline && outline.length >= 6 ? polyLine2Area(outline) : 0;
    let prevArea = spaceTypeScores[is] || 0;
    prevArea += area;
    spaceTypeScores[is] = prevArea;
  }

  for (const { type: roomType, id } of roomSet) {
    const result = secondaryClassifier[roomType];
    if (!result) {
      continue;
    }

    const { isInGroupOf } = result;
    const { outline } = rooms[id];
    const area = outline && outline.length >= 6 ? polyLine2Area(outline) : 0;

    assertIsNotUndefined(isInGroupOf);

    for (const secondary of isInGroupOf) {
      let prevArea = spaceTypeScores[secondary] || 0;
      prevArea += area;
      spaceTypeScores[secondary] = prevArea;
    }
  }

  const primarySpaceAreas: { spaceType: SpaceType; area: number }[] = [];
  for (const key in spaceTypeScores) {
    const area = spaceTypeScores[key];
    const spaceType = key as SpaceType;
    primarySpaceAreas.push({ spaceType, area });
  }
  primarySpaceAreas.sort((a, b) => b.area - a.area);

  const { spaceType: primarySpaceType } = primarySpaceAreas[0];

  for (const { type: roomType, id } of roomSet) {
    const result = secondaryClassifier[roomType];
    if (!result) {
      continue;
    }

    const { isInGroupOf } = result;
    let highestArea = 0;
    let indexWithMostArea = 0;
    for (const iString in isInGroupOf) {
      const i = parseInt(iString);
      const s = isInGroupOf[i];
      const area = spaceTypeScores[s];
      if (area !== undefined && area > highestArea) {
        highestArea = area;
        indexWithMostArea = i;
      }
    }

    assertIsNotUndefined(isInGroupOf);
    roomSpaceTypes[id] = isInGroupOf[indexWithMostArea];
  }

  let totalRoomSetArea = 0;
  for (const key in spaceTypeScores) {
    const area = spaceTypeScores[key];
    if (area === undefined) {
      continue;
    }
    totalRoomSetArea += area;
  }

  const diningArea = spaceTypeScores[SpaceType.DINING];
  const livingArea = spaceTypeScores[SpaceType.LIVING];
  const kitchenArea = spaceTypeScores[SpaceType.KITCHEN];

  let gatheringTypeCount = 0;
  let gatheringTotalArea = 0;
  if (diningArea !== undefined && diningArea > 0) {
    gatheringTypeCount++;
    gatheringTotalArea += diningArea;
  }
  if (livingArea !== undefined && livingArea > 0) {
    gatheringTypeCount++;
    gatheringTotalArea += livingArea;
  }
  if (kitchenArea !== undefined && kitchenArea > 0) {
    gatheringTypeCount++;
    gatheringTotalArea += kitchenArea;
  }

  const majoritySpaceType =
    gatheringTypeCount >= 2 && gatheringTotalArea > 0.5 * totalRoomSetArea ? SpaceType.GATHERING : primarySpaceType;

  return { majoritySpaceType, roomSpaceTypes };
}
